import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlagIcon from "@mui/icons-material/Flag";
import PublicIcon from "@mui/icons-material/Public";
import PlayoffIcon from "@mui/icons-material/EmojiEvents";
import { getRankInGroup, filterDriverRaces, getCleanAverageFeatureValue } from "../utils/raceUtils";
import { getDisplayTrackName } from "../utils/trackNameUtils";
import { scrollableTabsProps } from "../styles/responsive";
import { raceMonthShort } from "../utils/raceDate";


const FantasyDriverDetailsDrawer = ({ driver, groupDrivers, trackRaces, similarTrackRaces, allRaces, pastSeasonRaces, currentSeasonRaces, onClose }) => {
  const [tabValue, setTabValue] = useState(0); // 0: Track Analysis, 1: Track Type, 2: Complete Profile
  
  if (!driver) return null;

  // Helper function to determine if race is playoff (typically races 27-36)
  const isPlayoffRace = (raceNumber) => {
    return raceNumber >= 27;
  };

  // Helper function to get playoff round name
  const getPlayoffRound = (raceNumber) => {
    if (raceNumber >= 27 && raceNumber <= 29) return "Round of 16";
    if (raceNumber >= 30 && raceNumber <= 32) return "Round of 12";
    if (raceNumber >= 33 && raceNumber <= 35) return "Round of 8";
    if (raceNumber === 36) return "Final";
    return null;
  };


  // Helper function to get short team name
  const getShortTeamName = (fullTeamName) => {
    if (!fullTeamName) return "N/A";
    
    const teamMappings = {
      "Team Penske": "Penske",
      "Penske Motorsports": "Penske",
      "Joe Gibbs Racing": "JGR",
      "Hendrick Motorsports": "HMS",
      "Stewart-Haas Racing": "SHR",
      "Richard Childress Racing": "RCR",
      "Wood Brothers Racing": "Wood Bros",
      "Front Row Motorsports": "Front Row",
      "Roush Fenway Racing": "Roush",
      "Roush Fenway Keselowski Racing": "RFK",
      "23XI Racing": "23XI",
      "Trackhouse Racing": "Trackhouse",
      "Chip Ganassi Racing": "CGR",
      "Legacy Motor Club": "Legacy",
      "JTG Daugherty Racing": "JTG",
      "Spire Motorsports": "Spire",
      "BJ McLeod Motorsports": "BJM",
      "Rick Ware Racing": "RWR",
      "Kaulig Racing": "Kaulig",
      "Live Fast Motorsports": "Live Fast"
    };
    
    return teamMappings[fullTeamName] || fullTeamName.split(" ")[0];
  };

  // Helper function to get current track name from trackRaces
  const getCurrentTrackName = () => {
    if (trackRaces && trackRaces.length > 0) {
      return trackRaces[0].track_name || "This Track";
    }
    return "This Track";
  };

  // Helper function to get similar track names from similarTrackRaces
  const getSimilarTrackNames = () => {
    if (!similarTrackRaces || similarTrackRaces.length === 0) {
      return [];
    }
    
    // Get unique track names from similar track races, excluding the current track
    const currentTrack = getCurrentTrackName();
    const similarTracks = [...new Set(
      similarTrackRaces
        .map(race => race.track_name)
        .filter(trackName => trackName && trackName !== currentTrack)
    )].sort();
    
    return similarTracks;
  };


  // Helper function to format race name for display
  const formatRaceDisplay = (race, includeYear = false) => {
    if (!race.race_name) return { baseText: "N/A", isPlayoff: false, playoffRound: null, teamName: null };
    
    const isPlayoff = race.race_number ? isPlayoffRace(race.race_number) : false;
    const playoffRound = isPlayoff && race.race_number ? getPlayoffRound(race.race_number) : null;
    const teamName = race.team_name ? getShortTeamName(race.team_name) : null;
    
    // Use full display name (removes branding but keeps full name like "Atlanta Motor Speedway")
    const displayTrackName = getDisplayTrackName(race.race_name);
    
    // Format with year and month for better distinction between multiple races
    let baseText = displayTrackName;
    if (includeYear && race.season_year) {
      if (race.race_date) {
        const month = raceMonthShort(race.race_date);
        baseText = `${displayTrackName} (${month} ${race.season_year})`;
      } else {
        baseText = `${displayTrackName} (${race.season_year})`;
      }
    }
    
    return { baseText, isPlayoff, playoffRound, teamName };
  };

  // Data selection based on current tab
  const getTabData = () => {
    switch (tabValue) {
      case 0: // Track Analysis - This specific track only
        return {
          races: trackRaces,
          historicalRaces: trackRaces,
          title: `📍 ${getCurrentTrackName()} Analysis`,
          includeYear: true,
          showTeam: false
        };
      case 1: // Track Type - Similar tracks
        return {
          races: similarTrackRaces,
          historicalRaces: similarTrackRaces,
          title: `🏁 Track Type Performance`,
          includeYear: true,
          showTeam: true
        };
      case 2: // Complete Profile - All tracks
        return {
          races: allRaces,
          historicalRaces: currentSeasonRaces,
          title: `🌍 Complete NASCAR Profile`,
          includeYear: false,
          showTeam: false
        };
      default:
        return {
          races: trackRaces,
          historicalRaces: trackRaces,
          title: `📍 ${getCurrentTrackName()} Analysis`,
          includeYear: false,
          showTeam: false
        };
    }
  };

  const { races: selectedRaces, historicalRaces: selectedHistoricalRaces, title: currentTabTitle, includeYear, showTeam } = getTabData();

  const driverTrackRaces = filterDriverRaces(selectedRaces, driver, false, false);
  const driverHistoricalRaces = filterDriverRaces(selectedHistoricalRaces, driver, false, false);
  const driverCurrentSeasonRaces = filterDriverRaces(currentSeasonRaces, driver, false, false);
  const driverPastSeasonRaces = filterDriverRaces(pastSeasonRaces, driver, false, false);
  const groupDriverAverages = groupDrivers.map(groupDriver => {
    const driverRaces = selectedRaces.filter(race => race.driver_name === groupDriver);
    return {
      driver: groupDriver,
      race_pos: getCleanAverageFeatureValue("race_pos", driverRaces),
      quali_pos: getCleanAverageFeatureValue("quali_pos", driverRaces),
      driver_rating: getCleanAverageFeatureValue("driver_rating", driverRaces),
      fantasy_points: getCleanAverageFeatureValue("fantasy_points", driverRaces),
    };
  });

  const stats = {
    // Current tab specific stats
    avgFinish: getCleanAverageFeatureValue("race_pos", driverTrackRaces),
    avgStart: getCleanAverageFeatureValue("quali_pos", driverTrackRaces),
    avgRating: getCleanAverageFeatureValue("driver_rating", driverTrackRaces),
    avgFantasy: getCleanAverageFeatureValue("fantasy_points", driverTrackRaces),
    finishRank: getRankInGroup("race_pos", driver, groupDriverAverages),
    startRank: getRankInGroup("quali_pos", driver, groupDriverAverages),
    ratingRank: getRankInGroup("driver_rating", driver, groupDriverAverages),
    fantasyRank: getRankInGroup("fantasy_points", driver, groupDriverAverages),
    // Season context stats
    seasonFinish: getCleanAverageFeatureValue("race_pos", driverCurrentSeasonRaces),
    seasonStart: getCleanAverageFeatureValue("quali_pos", driverCurrentSeasonRaces),
    seasonRating: getCleanAverageFeatureValue("driver_rating", driverCurrentSeasonRaces),
    seasonFantasy: getCleanAverageFeatureValue("fantasy_points", driverCurrentSeasonRaces),
    pastSeasonFinish: getCleanAverageFeatureValue("race_pos", driverPastSeasonRaces),
    pastSeasonStart: getCleanAverageFeatureValue("quali_pos", driverPastSeasonRaces),
    pastSeasonRating: getCleanAverageFeatureValue("driver_rating", driverPastSeasonRaces),
    pastSeasonFantasy: getCleanAverageFeatureValue("fantasy_points", driverPastSeasonRaces),
    // Sample size
    sampleSize: driverTrackRaces.length,
  };

  // Get all races sorted by date (most recent first) - include all races, even multiple per track/year
  const allHistoricalRaces = selectedHistoricalRaces
    .map(race => ({
      race_date: race.race_date,
      track_name: race.track_name,
      season_year: race.season_year,
      race_number: race.race_number
    }))
    .filter((race, index, array) => 
      // Remove true duplicates (same date, track, year, race_number) but keep multiple races per track/year
      array.findIndex(r => 
        r.race_date === race.race_date && 
        r.track_name === race.track_name && 
        r.race_number === race.race_number
      ) === index
    )
    .sort((a, b) => new Date(b.race_date) - new Date(a.race_date));
  
  // Only show races that actually exist - no empty/N/A rows
  const maxRaces = Math.min(10, allHistoricalRaces.length); // Cap at 10 but only show what exists
  const pastRaces = Array.from({ length: maxRaces }, (_, num) => {
    // Get race info from the sorted list
    const raceInfo = allHistoricalRaces[num];
    const raceForDate = selectedHistoricalRaces.find(race => 
      race.race_date === raceInfo?.race_date && 
      race.track_name === raceInfo?.track_name &&
      race.race_number === raceInfo?.race_number
    );
    
    // Get driver-specific race data to extract team name
    const driverRaceData = driverHistoricalRaces.find(race => 
      race.race_date === raceInfo?.race_date && 
      race.track_name === raceInfo?.track_name &&
      race.race_number === raceInfo?.race_number
    );
    
    const raceData = {
      race_name: raceForDate?.track_name,
      season_year: raceForDate?.season_year,
      race_number: raceForDate?.race_number,
      race_date: raceForDate?.race_date,
      team_name: driverRaceData?.team_name, // Get team from driver's actual race data
    };
    
    const formattedRace = formatRaceDisplay(raceData, includeYear);
    
    return {
      num: `Race -${num}`,
      // Driver-specific data (will show "-" if driver didn't participate)
      finish: driverRaceData?.race_pos || "-",
      start: driverRaceData?.quali_pos || "-",
      rating: driverRaceData?.driver_rating || "-",
      fantasy: driverRaceData ? 
        ((driverRaceData.race_finish_points || 0) + (driverRaceData.race_stage_points || 0)) : "-",
      // Race name and additional display info
      race_name: formattedRace.baseText || raceData.race_name || "Unknown",
      isPlayoff: formattedRace.isPlayoff || false,
      playoffRound: formattedRace.playoffRound || null,
      teamName: showTeam ? formattedRace.teamName : null,
    };
  });

  return (
    <Drawer anchor="right" open={Boolean(driver)} onClose={onClose}>
      <Box sx={{ width: "60vw", p: 3, bgcolor: "background.paper" }}>
        
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">{driver}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Analysis Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => setTabValue(newValue)}
            {...scrollableTabsProps}
            sx={{
              "& .MuiTabs-flexContainer": { justifyContent: { xs: "flex-start", md: "center" } },
              "& .MuiTab-root": {
                minWidth: { xs: "90px", sm: "120px" },
                fontWeight: 500,
                fontSize: "0.9rem",
              },
              "& .Mui-selected": {
                fontWeight: "bold",
              },
            }}
          >
            <Tab 
              icon={<LocationOnIcon />} 
              label="Track Analysis"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<FlagIcon />} 
              label="Track Type"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<PublicIcon />} 
              label="Complete Profile"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {/* Tab Content Header with Similar Tracks Tooltip */}
        {tabValue === 1 ? (
          <Tooltip 
            title={
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  🏁 Similar Track Types Included:
                </Typography>
                {getSimilarTrackNames().length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      • {getCurrentTrackName()} (Next Race)
                    </Typography>
                    {getSimilarTrackNames().map((track, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {track}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    No similar tracks found for comparison
                  </Typography>
                )}
              </Box>
            }
            arrow
            placement="top"
            sx={{ 
              '& .MuiTooltip-tooltip': { 
                maxWidth: 400, 
                fontSize: '0.875rem',
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 3
              }
            }}
          >
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              sx={{ 
                mb: 2, 
                textAlign: "center",
                cursor: "help",
                textDecoration: "underline dotted",
                textDecorationColor: "text.secondary"
              }}
            >
              {currentTabTitle}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: "center" }}>
            {currentTabTitle}
          </Typography>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2}>
          {[
            { 
              title: currentTabTitle.split(' ').slice(1).join(' '), // Remove emoji from title
              data: [
                { label: "Avg Finish", value: stats.avgFinish },
                { label: "Avg Start", value: stats.avgStart },
                { label: "Avg Rating", value: stats.avgRating },
                { label: "Avg Fantasy Pts", value: stats.avgFantasy },
                { label: "Sample Size", value: `${stats.sampleSize} races` }
              ],
              color: "primary.main"
            },
            { 
              title: "Group Rankings", 
              data: [
                { label: "Finish Rank", value: `${stats.finishRank}/${groupDrivers.length}` },
                { label: "Start Rank", value: `${stats.startRank}/${groupDrivers.length}` },
                { label: "Rating Rank", value: `${stats.ratingRank}/${groupDrivers.length}` },
                { label: "Fantasy Rank", value: `${stats.fantasyRank}/${groupDrivers.length}` }
              ],
              color: "secondary.main"
            },
            { 
              title: `${currentSeasonRaces[0]?.season_year ?? ""} Current Form`.trim(), 
              data: [
                { label: "Avg Finish", value: stats.seasonFinish },
                { label: "Avg Start", value: stats.seasonStart },
                { label: "Avg Rating", value: stats.seasonRating },
                { label: "Avg Fantasy Points", value: stats.seasonFantasy }
              ],
              color: "success.main"
            },
            { 
              title: `${pastSeasonRaces[0]?.season_year ?? ""} Comparison`.trim(), 
              subtitle: "Same point in season",
              data: [
                { label: "Avg Finish", value: stats.pastSeasonFinish },
                { label: "Avg Start", value: stats.pastSeasonStart },
                { label: "Avg Rating", value: stats.pastSeasonRating },
                { label: "Avg Fantasy Points", value: stats.pastSeasonFantasy }
              ],
              color: "warning.main"
            }
          ].map((section, index) => (
            <Grid item xs={3} key={index}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: "100%",
                  borderLeft: `4px solid`,
                  borderLeftColor: section.color,
                }}
              >
                <CardContent>
                  <Typography 
                    variant="h6" 
                    fontWeight="bold" 
                    sx={{ 
                      color: section.color,
                      fontSize: "0.9rem",
                      mb: section.subtitle ? 0.5 : 1
                    }}
                  >
                    {section.title}
                  </Typography>
                  {section.subtitle && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        mb: 1,
                        display: "block"
                      }}
                    >
                      {section.subtitle}
                    </Typography>
                  )}
                  {section.data.map((item, idx) => (
                    <Typography key={idx} sx={{ fontSize: 14 }}>
                      <strong>{item.label}:</strong> {item.value}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Previous Races Table */}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Recent Race History
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 2 }}>Race</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 2, textAlign: "center" }}>Finish</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 2, textAlign: "center" }}>Start</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 2, textAlign: "center" }}>Rating</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 2, textAlign: "center" }}>Fantasy Pts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pastRaces.map((race, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      {/* Race name */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: "text.primary"
                        }}
                      >
                        {getDisplayTrackName(race.race_name)}
                      </Typography>
                      
                      {/* Playoff icon */}
                      {race.isPlayoff && race.playoffRound && (
                        <Tooltip title={race.playoffRound} arrow>
                          <PlayoffIcon 
                            sx={{ 
                              fontSize: 16, 
                              color: "warning.main",
                              cursor: "help"
                            }} 
                          />
                        </Tooltip>
                      )}
                      
                      {/* Team name chip when showTeam is enabled */}
                      {showTeam && race.teamName && race.finish !== "-" && (
                        <Chip
                          label={race.teamName}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: "0.75rem",
                            height: 20,
                            bgcolor: "primary.light",
                            color: "primary.dark",
                            fontWeight: 600,
                            "& .MuiChip-label": {
                              px: 1
                            }
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: race.finish !== "-" ? 500 : 400 }}>
                      {race.finish}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: race.start !== "-" ? 500 : 400 }}>
                      {race.start}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: race.rating !== "-" ? 500 : 400 }}>
                      {race.rating}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: race.fantasy !== "-" ? 500 : 400 }}>
                      {race.fantasy}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Drawer>
  );
};

export default FantasyDriverDetailsDrawer;